/**
 * @fileoverview 市场大厅页面
 * @description 展示所有预测市场，支持分类筛选、搜索、排序
 * 顶部展示最近浏览的市场（个性化推荐），下方为完整市场列表
 * 设计风格：币安深黑配色，分类标签页 + 卡片网格布局
 * @module pages/Markets
 */

import { BarChart2, BarChart3, Clock, Filter, Search, X } from 'lucide-react';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'wouter';

import MainLayout from '../components/layout/MainLayout';
import MarketCard from '../components/market/MarketCard';
import HighFreqMarketCard from '../components/market/HighFreqMarketCard';
import MarketFilter from '../components/market/MarketFilter';
import EmptyState from '../components/common/EmptyState';

import { getAssetIconUrl } from '../lib/utils';
import { useMarketStore } from '../stores/useMarketStore';
import { useRecentMarkets } from '../hooks/useRecentMarkets';
import type { Market } from '../types';

// ============================================================
// 子组件：最近浏览推荐区
// ============================================================

/**
 * 最近浏览市场推荐区域
 * @description 展示用户最近访问过的 2-3 个市场，无记录时不渲染
 */
function RecentMarketsSection() {
  const { t } = useTranslation();
  // 使用真实 store 数据替代 mock 数据
  const { markets } = useMarketStore();
  const { recentMarkets, clearHistory } = useRecentMarkets(markets);

  // 无浏览记录时不展示
  if (recentMarkets.length === 0) return null;

  return (
    <div className="mb-6">
      {/* 标题行 */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Clock className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm font-semibold text-foreground">{t('markets.recentViewed')}</span>
          <span
            className="text-[10px] px-2 py-0.5 rounded-full font-bold"
            style={{ background: 'rgba(240,185,11,0.12)', color: '#F0B90B' }}
          >
            {recentMarkets.length}
          </span>
        </div>
        <button
          onClick={clearHistory}
          className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
        >
          <X className="h-3 w-3" />
          {t('markets.clearHistory')}
        </button>
      </div>

      {/* 横向卡片列表 */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {recentMarkets.map((market) => (
          <Link key={market.id} href={`/markets/${market.id}`}>
            <div className="flex items-center gap-3 p-3 rounded-xl border border-white/8 bg-[#1E2026] hover:border-brand/30 hover:bg-[#252930] transition-all cursor-pointer group">
              {/* 分类图标 */}
              <div
                className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0 overflow-hidden"
                style={{ background: 'rgba(240,185,11,0.08)', border: '1px solid rgba(240,185,11,0.15)' }}
              >
                <img src={getAssetIconUrl(market.assetSymbol, market.category)} alt={market.assetSymbol || market.category} className="w-6 h-6 object-contain" />
              </div>

              {/* 市场信息 */}
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold text-foreground truncate group-hover:text-brand transition-colors">
                  {market.title}
                </p>
                <div className="flex items-center gap-2 mt-0.5">
                  <span className="text-[10px] text-green-400 font-mono">
                    {t('common.yes')} {market.yesPercent.toFixed(1)}%
                  </span>
                  <span className="text-[10px] text-muted-foreground">·</span>
                  <span className="text-[10px] text-red-400 font-mono">
                    {t('common.no')} {market.noPercent.toFixed(1)}%
                  </span>
                </div>
              </div>

              {/* 时间标签 */}
              <div
                className="text-[10px] px-2 py-0.5 rounded-full shrink-0"
                style={{
                  background:
                    market.status === 'active'
                      ? 'rgba(14,203,129,0.1)'
                      : 'rgba(246,70,93,0.1)',
                  color: market.status === 'active' ? '#0ECB81' : '#F6465D',
                }}
              >
                {market.status === 'active' ? t('market.status.active') : t('market.status.ended')}
              </div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}

// ============================================================
// 主页面组件
// ============================================================

/**
 * 市场大厅页面主组件
 * @description 顶部个性化推荐 + 筛选栏 + 完整市场列表
 */
export default function Markets() {
  const { t } = useTranslation();
  const { fetchMarkets, getFilteredMarkets, isLoading, setFilters } = useMarketStore();
  const [location] = useLocation();

  // 从 URL query 参数初始化分类筛选
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const cat = params.get('category');
    if (cat) {
      setFilters({ category: cat as import('../types').MarketCategory });
    }
  }, [location, setFilters]);

  // 组件挂载时加载市场数据
  useEffect(() => {
    fetchMarkets();
  }, [fetchMarkets]);

  const filteredMarkets = getFilteredMarkets();

  return (
    <MainLayout>
      {/* 页面顶部横幅 */}
      <div className="bg-[#1E2026] border-b border-white/6">
        <div className="container py-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 mb-1.5">
                <BarChart3 className="h-5 w-5 text-brand" />
                <h1
                  className="text-xl font-bold text-foreground"
                  style={{ fontFamily: 'Space Grotesk, sans-serif' }}
                >
                  {t('nav.markets')}
                </h1>
              </div>
              <p className="text-sm text-muted-foreground">
                {t('markets.subtitle')}
              </p>
            </div>

            {/* 市场统计 */}
            <div className="flex items-center gap-4">
              {[
                {
                  label: t('markets.activeMarkets'),
                  value: filteredMarkets.filter((m) => m.status === 'active').length.toString(),
                },
                { label: t('markets.totalMarkets'), value: filteredMarkets.length.toString() },
              ].map((stat) => (
                <div key={stat.label} className="text-center">
                  <p className="text-xl font-bold text-brand font-mono">{stat.value}</p>
                  <p className="text-xs text-muted-foreground">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="container py-6">
        {/* 最近浏览推荐区（有记录时显示） */}
        <RecentMarketsSection />

        {/* 筛选栏 */}
        <div className="bg-[#1E2026] rounded-2xl p-4 border border-white/6 mb-6">
          <div className="flex items-center gap-2 mb-3">
            <Filter className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm font-medium text-muted-foreground">{t('markets.filterAndSort')}</span>
          </div>
          <MarketFilter />
        </div>

        {/* 市场列表 */}
        {isLoading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {Array.from({ length: 8 }).map((_, i) => (
              <div
                key={i}
                className="bg-[#1E2026] rounded-2xl p-4 border border-white/6 animate-pulse"
              >
                <div className="h-4 bg-white/8 rounded mb-3 w-1/3" />
                <div className="h-5 bg-white/8 rounded mb-2" />
                <div className="h-4 bg-white/8 rounded mb-4 w-3/4" />
                <div className="h-2 bg-white/8 rounded mb-3" />
                <div className="h-3 bg-white/8 rounded w-1/2" />
              </div>
            ))}
          </div>
        ) : filteredMarkets.length === 0 ? (
          <EmptyState
            icon={<BarChart2 className="h-8 w-8 text-muted-foreground opacity-40" />}
            title={t('common.noData')}
            description={t('markets.emptyDesc')}
          />
        ) : (
          <>
            {/* 结果统计 */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Search className="h-4 w-4 text-muted-foreground" />
                <p className="text-sm text-muted-foreground">
                  {t('markets.resultCount', { n: filteredMarkets.length })}
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 items-stretch">
              {filteredMarkets.map((market, index) =>
                market.marketType === 'high_freq' ? (
                  <HighFreqMarketCard key={market.id} market={market} animationDelay={index * 30} />
                ) : (
                  <MarketCard key={market.id} market={market} animationDelay={index * 30} />
                )
              )}
            </div>
          </>
        )}
      </div>
    </MainLayout>
  );
}
